// =============================================================================
// io/storage/media.ts — Supabase Storage, for screenshots, recordings, snapshots.
// -----------------------------------------------------------------------------
// Relocated from the old client's `writeBinary` / `readBinary` / `listSubdir`.
// One io layer, three surfaces: the trade form's screenshot carousel, the
// journal's chart screenshots, and voice notes.
//
// ── THE THREE BUCKETS, AND WHY THERE ARE ONLY THREE ──
//
//   screenshots  per-trade, per-pattern and per-journal image attachments
//   recordings   voice notes
//   snapshots    dashboard PNG exports
//
// The old client's helpers also accept `backups` and `performance reviews`,
// which are LOCAL artifacts from a pre-cloud era with no bucket behind them —
// it no-ops on those. This client never had them, so an unknown bucket is a
// programming error and throws rather than silently succeeding.
//
// ── 🚨 STORAGE OBJECTS ARE NOT BACKED UP ──
//
// Beta's own comment, and it is worth repeating where the uploads happen: the
// nightly database backup does NOT cover these buckets, and PITR is not enabled.
// A restore yields intact trades with dead media links. That is the reason
// `removeMedia` takes an explicit list and is never called speculatively.
//
// ── PATHS ARE `${uid}/${filename}`, AND THE UID IS NOT THE CALLER'S TO PASS ──
//
// It is read from the live session here. A caller-supplied uid is a caller that
// can be wrong about whose media it is writing, and RLS would then reject it at
// best or accept it at worst.
// =============================================================================

import type { SupabaseClient } from '@supabase/supabase-js';

export const MEDIA_BUCKETS = ['screenshots', 'recordings', 'snapshots'] as const;
export type MediaBucket = (typeof MEDIA_BUCKETS)[number];

export function isMediaBucket(name: string): name is MediaBucket {
  return (MEDIA_BUCKETS as readonly string[]).includes(name);
}

async function requireUid(sb: SupabaseClient): Promise<string> {
  const { data } = await sb.auth.getUser();
  const uid = data.user?.id;
  // ⚠ THROWS rather than returning null. The old client warns and returns,
  // which means an upload during a lapsed session reports success to the form,
  // the filename lands in `trade.screenshots[]`, and the image does not exist —
  // a broken thumbnail with no error anywhere.
  if (!uid) throw new Error('Not signed in — media cannot be read or written.');
  return uid;
}

function requireBucket(bucket: string): MediaBucket {
  if (!isMediaBucket(bucket)) {
    throw new Error(`Unknown media bucket "${bucket}" — expected one of ${MEDIA_BUCKETS.join(', ')}.`);
  }
  return bucket;
}

/**
 * A collision-proof filename.
 *
 * ── 🚨 THE OLD CLIENT'S SCHEME CAN OVERWRITE ──
 *
 * It names attachments `${prefix}-${current.length + 1}.${ext}` and uploads with
 * `upsert: true`. Delete the second of three screenshots and add another: the
 * array is length 2, the new file is named `-3`, and it SILENTLY REPLACES the
 * existing `-3`. The user sees two thumbnails of the same image and the original
 * is gone from a bucket that has no backup.
 *
 * The ordinal is kept because it makes the bucket readable, and a short random
 * token is appended so the name cannot repeat. Beta reads whatever filename the
 * row lists, so a longer name interoperates fine — nothing parses these.
 */
export function mediaFilename(prefix: string, ordinal: number, ext: string): string {
  const safe = String(prefix || 'file').replace(/[^a-z0-9._-]+/gi, '-').replace(/^-+|-+$/g, '');
  const token = Math.random().toString(36).slice(2, 8);
  return `${safe || 'file'}-${ordinal}-${token}.${ext}`;
}

/** The extension the old client picks, by blob type. */
export function screenshotExt(type: string): string {
  return type === 'image/webp' ? 'webp' : type === 'image/jpeg' ? 'jpg' : 'png';
}

/** The extension for a recorder blob — the recorder's own MIME decides. */
export function recordingExt(type: string): string {
  if (type.includes('mp4')) return 'm4a';
  if (type.includes('ogg')) return 'ogg';
  return 'webm';
}

/**
 * Upload, returning the filename to store on the row.
 *
 * ⚠ `upsert: true` matches the old client and is safe HERE because
 * `mediaFilename` cannot repeat. It is kept rather than set false so that a
 * retry after a network failure — same name, same bytes — succeeds instead of
 * failing on a half-written object.
 */
export async function uploadMedia(
  sb: SupabaseClient,
  bucket: string,
  filename: string,
  blob: Blob,
): Promise<string> {
  const b = requireBucket(bucket);
  const uid = await requireUid(sb);

  const { error } = await sb.storage.from(b).upload(`${uid}/${filename}`, blob, {
    upsert: true,
    contentType: blob.type || 'application/octet-stream',
  });

  // ⚠ Throws with the bucket and filename in the message. An upload that fails
  // quietly leaves the caller storing a filename for an object that is not there.
  if (error) throw new Error(`Upload failed for ${b}/${filename}: ${error.message}`);
  return filename;
}

/**
 * Download, or `null` when the object is not there.
 *
 * ⚠ A MISSING FILE IS NULL, NOT AN ERROR, and that is the old client's call for
 * a good reason: a row can legitimately reference an object that was removed, and
 * a thrown error there would break a whole trade form over one dead thumbnail.
 * Every OTHER failure throws — a permissions or network problem is not a missing
 * screenshot, and treating them alike hides an outage as an empty carousel.
 */
export async function downloadMedia(
  sb: SupabaseClient,
  bucket: string,
  filename: string,
): Promise<Blob | null> {
  const b = requireBucket(bucket);
  const uid = await requireUid(sb);

  const { data, error } = await sb.storage.from(b).download(`${uid}/${filename}`);
  if (error) {
    const msg = String(error.message || '').toLowerCase();
    if (msg.includes('not found') || msg.includes('does not exist')) return null;
    throw new Error(`Download failed for ${b}/${filename}: ${error.message}`);
  }
  return data;
}

/** Every object this user has in a bucket. */
export async function listMedia(sb: SupabaseClient, bucket: string): Promise<string[]> {
  const b = requireBucket(bucket);
  const uid = await requireUid(sb);

  const { data, error } = await sb.storage.from(b).list(`${uid}/`, {
    limit: 1000,
    sortBy: { column: 'name', order: 'asc' },
  });
  if (error) throw new Error(`List failed for ${b}: ${error.message}`);
  return (data ?? []).map((o) => o.name);
}

/**
 * Delete objects.
 *
 * ⚠ EXPLICIT LIST ONLY, never a prefix sweep. These buckets have no backup —
 * beta's own comment says so — and a prefix delete that matched one filename too
 * many is unrecoverable. The caller names exactly what goes.
 */
export async function removeMedia(
  sb: SupabaseClient,
  bucket: string,
  filenames: readonly string[],
): Promise<void> {
  if (!filenames.length) return;
  const b = requireBucket(bucket);
  const uid = await requireUid(sb);

  const { error } = await sb.storage.from(b).remove(filenames.map((f) => `${uid}/${f}`));
  if (error) throw new Error(`Delete failed for ${b}: ${error.message}`);
}
